import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import api from "../api";

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #0f172a;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.02em;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const KpiTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const KpiLabel = styled.span`
  color: #475569;
  font-size: 0.84rem;
  font-weight: 600;
`;

const KpiValue = styled.span`
  color: #0f172a;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
`;

const Trend = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${(props) => (props.$up ? "#16a34a" : "#dc2626")};
  background: ${(props) => (props.$up ? "#dcfce7" : "#fee2e2")};
  border-radius: 999px;
  padding: 4px 8px;
`;

const ListGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1300px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const ListCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 220px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const Primary = styled.span`
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 700;
`;

const Secondary = styled.span`
  color: #64748b;
  font-size: 0.78rem;
`;

const Empty = styled.div`
  color: #64748b;
  font-size: 0.85rem;
  padding: 20px 0;
`;

const Skeleton = styled.div`
  height: ${(props) => props.$h || 84}px;
  border-radius: 10px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f8fafc 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ErrorText = styled.div`
  background: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
  border-radius: 10px;
  padding: 12px;
  font-size: 0.86rem;
  font-weight: 600;
`;

const formatAmount = (value) =>
  `Rs ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const kpiConfig = [
  { key: "active_orders", label: "Active Orders" },
  { key: "pending_qc", label: "Pending QC" },
  { key: "low_stock", label: "Low Stock" },
  { key: "open_reworks", label: "Open Reworks" },
  { key: "workers_active", label: "Workers Active" },
  { key: "pending_payroll", label: "Pending Payroll" },
  { key: "expiring_docs", label: "Expiring Docs" },
  { key: "pending_quotations", label: "Pending Quotations" },
  { key: "monthly_revenue", label: "Monthly Revenue", money: true },
  { key: "job_cards_completed_this_month", label: "Job Cards Completed This Month" },
];

function AdminDashboardScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [discrepancies, setDiscrepancies] = useState([]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [dashboardRes, discrepancyRes] = await Promise.all([
          api.get("/admin/dashboard"),
          api.get("/reports/inventory-discrepancies", { params: { only_major: true, limit: 5 } }),
        ]);
        if (!active) return;
        setDashboard(dashboardRes?.data || null);
        setDiscrepancies(discrepancyRes?.data?.data || []);
      } catch (err) {
        if (!active) return;
        const message = err?.response?.data?.message || "Failed to load admin dashboard.";
        setError(message);
        setDiscrepancies([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const cards = useMemo(() => {
    const source = dashboard?.kpi_cards || {};
    return kpiConfig.map((item) => {
      const entry = source[item.key] || {};
      return {
        ...item,
        value: entry.value ?? 0,
        trend: Number(entry.trend_pct || 0),
      };
    });
  }, [dashboard]);

  const lowStock = dashboard?.lists?.low_stock_alerts || [];
  const overdue = dashboard?.lists?.overdue_job_cards || [];
  const pendingQuotations = dashboard?.lists?.pending_quotations || [];

  const renderCards = () => {
    if (loading) {
      return (
        <KpiGrid>
          {Array.from({ length: 10 }).map((_, idx) => (
            <Skeleton key={idx} />
          ))}
        </KpiGrid>
      );
    }

    return (
      <KpiGrid>
        {cards.map((card) => (
          <KpiCard key={card.key}>
            <KpiTop>
              <KpiLabel>{card.label}</KpiLabel>
              <Trend $up={card.trend >= 0}>{card.trend >= 0 ? "▲" : "▼"} {Math.abs(card.trend)}%</Trend>
            </KpiTop>
            <KpiValue>{card.money ? formatAmount(card.value) : formatNumber(card.value)}</KpiValue>
          </KpiCard>
        ))}
      </KpiGrid>
    );
  };

  const renderList = (items, renderer, emptyText) => {
    if (loading) {
      return (
        <>
          <Skeleton $h={58} />
          <Skeleton $h={58} />
          <Skeleton $h={58} />
        </>
      );
    }
    if (!items.length) return <Empty>{emptyText}</Empty>;
    return items.map(renderer);
  };

  return (
    <Page>
      {error ? <ErrorText>{error}</ErrorText> : null}

      <Section>
        <SectionTitle>Cross-Module KPI Overview</SectionTitle>
        {renderCards()}
      </Section>

      <Section>
        <SectionTitle>Action Panels</SectionTitle>
        <ListGrid>
          <ListCard>
            <SectionTitle>Low Stock Alerts</SectionTitle>
            {renderList(
              lowStock,
              (item) => (
                <Row key={item.material_id}>
                  <Primary>{item.material_name || item.material_code || "Material"}</Primary>
                  <Secondary>
                    Available: {formatNumber(item.available_qty)} {item.uom || ""}
                  </Secondary>
                  <Secondary>
                    Reorder: {formatNumber(item.reorder_level)} {item.uom || ""}
                  </Secondary>
                </Row>
              ),
              "No low stock alerts."
            )}
          </ListCard>

          <ListCard>
            <SectionTitle>Overdue Job Cards</SectionTitle>
            {renderList(
              overdue,
              (item) => (
                <Row key={item.job_card_id}>
                  <Primary>{item.job_card_no || `#${item.job_card_id}`}</Primary>
                  <Secondary>{item.design_name || "Design not set"}</Secondary>
                  <Secondary>Status: {item.status || "-"}</Secondary>
                </Row>
              ),
              "No overdue job cards."
            )}
          </ListCard>

          <ListCard>
            <SectionTitle>Pending Quotations</SectionTitle>
            {renderList(
              pendingQuotations,
              (item) => (
                <Row key={item.quotation_id}>
                  <Primary>{item.quotation_no || `#${item.quotation_id}`}</Primary>
                  <Secondary>{item.customer_name || "Customer not set"}</Secondary>
                  <Secondary>{formatAmount(item.amount)}</Secondary>
                </Row>
              ),
              "No pending quotations."
            )}
          </ListCard>

          <ListCard>
            <SectionTitle>Inventory Discrepancies</SectionTitle>
            {renderList(
              discrepancies,
              (item) => (
                <Row key={item.job_card_id}>
                  <Primary>{item.job_card_no || `#${item.job_card_id}`}</Primary>
                  <Secondary>{item.design_name || "Design not set"}</Secondary>
                  <Secondary>
                    {item.reconciliation_status} • {Number(item.total_discrepancy_pct || 0).toFixed(2)}%
                  </Secondary>
                </Row>
              ),
              "No major inventory discrepancies."
            )}
          </ListCard>
        </ListGrid>
      </Section>
    </Page>
  );
}

export default AdminDashboardScreen;
